import React from 'react';
import ArmorAvatar from './ArmorAvatar';
import PixelSprite from './PixelSprite';
import ProgressCelebrations from './ProgressCelebrations';
import SpellFx from './SpellFx';
import ImpactFx from './ImpactFx';
import bosses from '../data/bossSprites.json';
import { MANA_COST } from '../utils/bossBattle';
import { formatHours } from '../utils/events';
import { prefersReducedMotion } from '../hooks/useProgressFx';

const label = { fontSize: 9, lineHeight: '10px', fontWeight: 700, letterSpacing: '.06em', color: 'rgba(255,255,255,.5)', width: 16 };

function Bar({ name, pct, color, text, transition }) {
  return (
    <div className="flex items-center" style={{ gap: 6 }}>
      <span style={label}>{name}</span>
      <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#273C61' }}>
        <div style={{ height: 6, borderRadius: 3, background: color, width: `${Math.min(100, Math.max(0, pct))}%`, transition: `width ${transition}s` }} />
      </div>
      <span className="tabular-nums whitespace-nowrap text-right" style={{ fontSize: 10, color: 'rgba(255,255,255,.8)', width: 42 }}>{text}</span>
    </div>
  );
}

// Boss battle i baren: arena med avatar og boss, stats og CAST. Tom tilstand, når der ikke er et fast-pris-projekt.
export default function BossBattle({ boss, stage }) {
  const { project, monster, stats, mana, spellsReady, defeated, justDefeated, casting, canCast, cast, spell, castKey, hitKey, dmg, manaFloat, banner, burstKey } = boss;
  const empty = !project;
  const sprite = monster !== null ? bosses.monsters[monster] : null;
  const calm = prefersReducedMotion();
  const melee = spell?.kind === 'melee';
  const manaNow = Math.min(mana, MANA_COST);
  const castTitle = empty ? 'Log time on a fixed-price project to summon a boss'
    : defeated ? 'Boss defeated'
      : canCast ? `Cast spell · ${formatHours(MANA_COST)} damage`
        : casting ? undefined : `${formatHours(MANA_COST - manaNow)} more mana to cast`;

  const shaking = !calm && spell?.shake && hitKey > 0;
  const arenaStyle = { width: 300, height: 71, borderRadius: 8, background: '#111e2e', border: '1px solid #273C61', boxSizing: 'border-box', cursor: canCast ? 'pointer' : undefined, animation: shaking ? `${hitKey % 2 ? 'zrmShake' : 'zrmShakeB'} .4s` : undefined };

  let bossStyle;
  if (defeated) bossStyle = justDefeated ? { animation: 'zrmSink 1s forwards' } : { opacity: 0 };
  else bossStyle = hitKey > 0 && !calm ? { animation: 'zrmShake .4s, zrmHitFlash .4s steps(2)' } : undefined;

  return (
    <div className="relative flex items-center flex-none" style={{ gap: 12 }} title={sprite ? `Boss battle · ${sprite.name}: ${sprite.tagline_da}` : 'Boss battle · the fixed-price project you have logged most hours on'}>
      <div className="relative flex-none" style={arenaStyle} onClick={canCast ? cast : undefined}>
        <span
          className="absolute uppercase font-semibold overflow-hidden whitespace-nowrap"
          style={{ left: 8, right: 76, top: 5, fontSize: 10, lineHeight: '12px', letterSpacing: '.06em', color: 'rgba(255,255,255,.5)', textOverflow: 'ellipsis' }}
          title={project?.name}
        >
          {empty ? 'No fixed-price project this week' : project.name}
        </span>
        <div className="absolute" style={{ left: 8, right: 8, top: 62, height: 1, background: '#273C61' }} />

        <div className="zrm-bob absolute" style={{ left: 16, top: 18 }}>
          <div
            key={castKey}
            style={{ transformOrigin: '50% 100%', '--dx': '176px', animation: calm || !spell ? undefined : melee ? 'zrmDash 1s ease-in-out' : 'zrmCastPose .5s' }}
          >
            <ArmorAvatar stage={stage} cell={2} />
          </div>
        </div>

        {!empty && sprite && (
          <>
            <div className="absolute" style={{ left: 228, top: 8, width: 60, height: 3, borderRadius: 2, background: '#273C61' }}>
              <div style={{ height: 3, borderRadius: 2, background: '#FF7800', width: `${stats.hpPct}%`, transition: 'width .6s ease-out' }} />
            </div>
            <div className="absolute" style={{ left: 228, top: 14, transformOrigin: '50% 100%', animation: defeated ? undefined : 'zrmBob 2s steps(2) infinite' }}>
              <div key={defeated ? 'dead' : hitKey} style={{ transformOrigin: '50% 100%', ...bossStyle }}>
                <PixelSprite map={sprite.map} palette={bosses.palette} cell={3} />
              </div>
            </div>
            {defeated && (
              <span className="absolute text-center font-bold uppercase" style={{ left: 216, width: 84, top: 30, fontSize: 9, letterSpacing: '.2em', color: '#FF7800', animation: justDefeated ? 'zrmPop .5s .7s both' : undefined }}>
                Defeated
              </span>
            )}
          </>
        )}

        {!calm && spell && casting && <SpellFx key={`fx-${castKey}`} spell={spell} />}
        {!calm && spell && hitKey > 0 && <ImpactFx key={`hit-${hitKey}`} spell={spell} />}
        {dmg && <span key={dmg.id} className="absolute font-bold whitespace-nowrap" style={{ left: 242, top: 14, fontSize: 12, color: '#FF7800', animation: 'zrmFloat 1s forwards', pointerEvents: 'none' }}>{dmg.text}</span>}
        {spell && casting && (
          <span key={`name-${castKey}`} className="absolute uppercase font-bold whitespace-nowrap" style={{ left: 56, top: 20, fontSize: 10, lineHeight: '12px', letterSpacing: '.1em', color: spell.glow && spell.glow !== 'transparent' ? spell.glow : spell.color, animation: 'zrmFloat 1.3s forwards', pointerEvents: 'none' }}>
            {spell.name}
          </span>
        )}
        {manaFloat && <span key={manaFloat.id} className="absolute font-bold whitespace-nowrap" style={{ left: 14, top: 16, fontSize: 11, color: '#94C2FF', animation: 'zrmFloat 1.2s forwards', pointerEvents: 'none' }}>{manaFloat.text}</span>}
      </div>

      <div className="flex flex-col flex-none" style={{ width: 150, gap: 5 }}>
        <span className="font-bold whitespace-nowrap" style={{ fontSize: 14, lineHeight: '16px' }}>{empty ? '—' : sprite?.name || ' '}</span>
        <span className="whitespace-nowrap" style={{ fontSize: 10, lineHeight: '12px', color: 'rgba(255,255,255,.5)' }}>
          {empty ? '—' : `Fixed price · ${formatHours(stats.budget)} budget`}
        </span>
        <Bar name="HP" pct={empty ? 0 : stats.hpPct} color="#FF7800" text={empty ? '—' : formatHours(stats.hp)} transition=".6" />
        <Bar name="MP" pct={empty ? 0 : (manaNow / MANA_COST) * 100} color="#94C2FF" text={empty ? '—' : `${formatHours(manaNow)}/${MANA_COST}h`} transition=".4" />
      </div>

      <button
        onClick={cast}
        disabled={!canCast}
        title={castTitle}
        aria-label="Cast spell"
        className="relative flex flex-col items-center justify-center flex-none"
        style={{
          width: 60, height: 60, borderRadius: 8, boxSizing: 'border-box', gap: 2,
          background: canCast || casting ? '#FF7800' : 'transparent',
          border: `1px solid ${canCast || casting ? '#FF7800' : '#273C61'}`,
          color: canCast || casting ? '#0D1A27' : 'rgba(255,255,255,.35)',
          cursor: canCast ? 'pointer' : 'default',
          animation: canCast && !calm ? 'zrmRing 1.6s ease-out infinite' : undefined,
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
        <span style={{ fontSize: 9, lineHeight: '10px', fontWeight: 700, letterSpacing: '.12em' }}>CAST</span>
        {spellsReady >= 2 && (
          <span className="absolute font-bold" style={{ top: -7, right: -7, height: 18, minWidth: 18, padding: '0 4px', borderRadius: 9, background: '#94C2FF', color: '#0D1A27', fontSize: 10, lineHeight: '18px', boxSizing: 'border-box' }}>
            {'×'}{spellsReady}
          </span>
        )}
      </button>

      <ProgressCelebrations banner={banner} burstKey={burstKey} />
    </div>
  );
}
